import type { ApiClient } from '@hetivo/hetivo-lib-front-utils';
import { toPaginatedResponse, toQueryParams } from '@hetivo/hetivo-lib-front-utils';
import type {
  GeoCoordinates,
  Location,
  LocationStatus,
  PaginatedResponse,
  PaginationParams,
  HetivoApiResponse,
  HetivoPaginatedResponse,
  PhoneNumber,
  PostalAddress,
  SortParams,
} from '@hetivo/hetivo-lib-types';

import type { LocationSortableColumn } from '../types/location.types';

// ─── Params ───────────────────────────────────────────────────────────────────

export interface SearchLocationsParams extends PaginationParams {
  excludeIds?: string[];
  ids?: string[];
  name?: string;
  sort?: SortParams<LocationSortableColumn>;
  territoryIds?: string[];
}

export interface CreateLocationParams {
  address?: Partial<PostalAddress>;
  color: string;
  coordinates?: GeoCoordinates;
  customerSupportPhone?: PhoneNumber;
  description?: string;
  geocodingScore?: number;
  geocodingSource?: 'autocomplete' | 'manual';
  name: string;
  phone?: PhoneNumber;
  status?: LocationStatus;
  territoryId: string;
}

export interface UpdateLocationParams {
  address?: Partial<PostalAddress>;
  color?: string;
  coordinates?: GeoCoordinates;
  customerSupportPhone?: PhoneNumber | null;
  geocodingScore?: number;
  geocodingSource?: 'autocomplete' | 'manual';
  name?: string;
  phone?: PhoneNumber | null;
  status?: LocationStatus;
}

// ─── API Factory ──────────────────────────────────────────────────────────────

export type LocationApi = {
  createLocation: (params: CreateLocationParams) => Promise<Location>;
  getLocation: (id: string) => Promise<Location>;
  searchLocations: (params: SearchLocationsParams) => Promise<PaginatedResponse<Location>>;
  updateLocation: (id: string, params: UpdateLocationParams) => Promise<Location>;
};

export function createLocationApi(client: ApiClient): LocationApi {
  return {
    createLocation: async (params: CreateLocationParams): Promise<Location> => {
      const response = await client.execute<HetivoApiResponse<Location>>(() =>
        client.instance.post('/locations', params),
      );
      if (!response.data) {
        throw new Error('createLocation: unexpected empty response');
      }
      return response.data;
    },

    getLocation: async (id: string): Promise<Location> => {
      const response = await client.execute<HetivoApiResponse<Location>>(() => client.instance.get(`/locations/${id}`));
      if (!response.data) {
        throw new Error('getLocation: unexpected empty response');
      }
      return response.data;
    },

    searchLocations: async (params: SearchLocationsParams): Promise<PaginatedResponse<Location>> => {
      const body = toSearchBody(params);
      const query = toQueryParams(params);
      const response = await client.execute<HetivoApiResponse<HetivoPaginatedResponse<Location>>>(() =>
        client.instance.post('/locations/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('searchLocations: unexpected empty response');
      }
      return toPaginatedResponse(response.data);
    },

    updateLocation: async (id: string, params: UpdateLocationParams): Promise<Location> => {
      const response = await client.execute<HetivoApiResponse<Location>>(() =>
        client.instance.patch(`/locations/${id}`, params),
      );
      if (!response.data) {
        throw new Error('updateLocation: unexpected empty response');
      }
      return response.data;
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSearchBody(params: SearchLocationsParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const trimmedName = params.name?.trim();

  const conditionalFields: [string, unknown][] = [
    ['excludeIds', params.excludeIds],
    ['ids', params.ids],
    ['name', trimmedName],
    ['territoryIds', params.territoryIds],
  ];

  for (const [key, value] of conditionalFields) {
    if (Array.isArray(value) ? value.length > 0 : value) {
      body[key] = value;
    }
  }

  return body;
}
